import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Renderer2
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {provideEchartsCore} from 'ngx-echarts';
import * as echarts from 'echarts/core';
import {SankeyChart} from 'echarts/charts';
import {DataZoomComponent, GridComponent, TooltipComponent} from 'echarts/components';
import {CanvasRenderer} from 'echarts/renderers';
import {selectLoading, selectPapersByProjectId} from '../../services/research-paper/research-paper.selectors';
import {loadResearchPapers} from '../../services/research-paper/research-paper.actions';
import {ResearchPaperDTO} from '../../dto/research-paper';
import {of} from 'rxjs';
import {Store} from '@ngrx/store';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatToolbarModule} from '@angular/material/toolbar';
import {RouterLink} from '@angular/router';
import {selectSelectedProject} from '../../../project/service/project.selectors';
import {ProjectDTO} from '../../../project/dto/project';
import {ResearchPrismaFlowComponent} from '../research-prisma-flow/research-prisma-flow.component';
import {MatTabsModule} from '@angular/material/tabs';
import {paperAuditedScreening, paperPassedScreening} from '../../helper/research-paper';
import {selectSettingByProjectId} from '../../services/screening-settings/screening-settings.selectors';
import {ScreeningSettingsDTO} from '../../dto/screening-settings';
import {loadScreeningSettings} from '../../services/screening-settings/screening-settings.actions';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {switchMap} from 'rxjs/operators';

echarts.use([SankeyChart, GridComponent, CanvasRenderer, TooltipComponent, DataZoomComponent]);

enum ScreeningStatus {
  ANALYZED_SCREENING1YES = 'Analyzed Title/Abstract screening - yes',
  ANALYZED_SCREENING1NO = 'Analyzed Title/Abstract screening - no',
  SCREENING1Yes = 'Predicted Title/Abstract screening - yes',
  SCREENING1NO = 'Predicted Title/Abstract screening - no',
  PDF_DOWNLOAD_NO = 'PDF Downloaded - no',
  PDF_DOWNLOAD_YES = 'PDF Downloaded - yes',
  ANALYZED_SCREENING2Yes = 'Analyzed Full-Text Screening - yes',
  ANALYZED_SCREENING2No = 'Analyzed Full-Text Screening - no',
  SCREENING2Yes = 'Predicted Full-Text screening - yes',
  SCREENING2NO = 'Predicted Full-Text screening - no',
}

interface Links {
  source: string;
  target: string;
  value: number;
}


@Component({
  selector: 'app-research-paper-screening-flow',
  imports: [
    CommonModule,
    MatProgressSpinner,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    RouterLink,
    ResearchPrismaFlowComponent,
    MatTabsModule
  ],
  templateUrl: './research-paper-screening-flow.component.html',
  styleUrl: './research-paper-screening-flow.component.scss',
  providers: [
    provideEchartsCore({echarts}),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResearchPaperScreeningFlowComponent {
  private readonly store: Store = inject(Store);
  private renderer: Renderer2 = inject(Renderer2);

  readonly project = this.store.selectSignal(selectSelectedProject);
  readonly projectId = computed(() => this.project()?.id);
  readonly loading = this.store.selectSignal(selectLoading());
  readonly papers = toSignal(
    toObservable(this.projectId).pipe(
      switchMap(projectId => projectId ? this.store.select(selectPapersByProjectId(projectId)) : of([] as ResearchPaperDTO[]))
    ),
    {initialValue: [] as ResearchPaperDTO[]}
  );
  readonly settings = toSignal(
    toObservable(this.projectId).pipe(
      switchMap(projectId => projectId ? this.store.select(selectSettingByProjectId(projectId)) : of(undefined as ScreeningSettingsDTO | undefined))
    ),
    {initialValue: undefined as ScreeningSettingsDTO | undefined}
  );
  readonly flowData = computed(() => this.getData(this.papers()));
  readonly chartOption = computed(() => this.generateChart(this.flowData()));

  constructor() {
    effect(() => {
      const projectId = this.projectId();
      if (!projectId) return;
      this.store.dispatch(loadResearchPapers({projectId}));
      this.store.dispatch(loadScreeningSettings({projectId}));
    });
  }

  private getData(papers: ResearchPaperDTO[]): {data: { name: string; }[]; links: Links[]; paperTitles: string[]} {
    const data = Object.values(ScreeningStatus).map(status => ({name: status.toString()}));
    const paperTitles: string[] = [];

    const links = [
      {
        source: ScreeningStatus.ANALYZED_SCREENING1YES.toString(),
        target: ScreeningStatus.SCREENING1Yes.toString(),
        value: 0
      },
      {
        source: ScreeningStatus.ANALYZED_SCREENING1YES.toString(),
        target: ScreeningStatus.SCREENING1NO.toString(),
        value: 0
      },
      {
        source: ScreeningStatus.SCREENING1Yes,
        target: ScreeningStatus.PDF_DOWNLOAD_NO,
        value: 0
      },
      {
        source: ScreeningStatus.SCREENING1Yes,
        target: ScreeningStatus.PDF_DOWNLOAD_YES,
        value: 0
      },
      {
        source: ScreeningStatus.PDF_DOWNLOAD_YES,
        target: ScreeningStatus.ANALYZED_SCREENING2Yes,
        value: 0
      },
      {
        source: ScreeningStatus.PDF_DOWNLOAD_YES,
        target: ScreeningStatus.ANALYZED_SCREENING2No,
        value: 0
      },
      {
        source: ScreeningStatus.ANALYZED_SCREENING2Yes,
        target: ScreeningStatus.SCREENING2Yes,
        value: 0
      },
      {
        source: ScreeningStatus.ANALYZED_SCREENING2Yes,
        target: ScreeningStatus.SCREENING2NO,
        value: 0
      }
    ];

    papers.forEach(paper => {
      let title = paper.title;
      if (paperTitles.includes(title)) {
        title = title + ' (' + paper.id + ')';
      }
      paperTitles.push(title);
      data.push({
        name: title
      });

      if (paperAuditedScreening(paper)) {
        links.push({
          source: title,
          target: ScreeningStatus.ANALYZED_SCREENING1YES,
          value: 1
        });


        if (paperPassedScreening(paper)) {
          this.increaseLink(links, ScreeningStatus.ANALYZED_SCREENING1YES, ScreeningStatus.SCREENING1Yes);

          if (paper.hasDownloadedPdf) {
            this.increaseLink(links, ScreeningStatus.SCREENING1Yes, ScreeningStatus.PDF_DOWNLOAD_YES);
            if (paperAuditedScreening(paper, true)) {
              this.increaseLink(links, ScreeningStatus.PDF_DOWNLOAD_YES, ScreeningStatus.ANALYZED_SCREENING2Yes);
              if (paperPassedScreening(paper, true)) {
                this.increaseLink(links, ScreeningStatus.ANALYZED_SCREENING2Yes, ScreeningStatus.SCREENING2Yes);
              } else {
                this.increaseLink(links, ScreeningStatus.ANALYZED_SCREENING2Yes, ScreeningStatus.SCREENING2NO);
              }

            } else {
              this.increaseLink(links, ScreeningStatus.PDF_DOWNLOAD_YES, ScreeningStatus.ANALYZED_SCREENING2No);
            }

          } else {
            this.increaseLink(links, ScreeningStatus.SCREENING1Yes, ScreeningStatus.PDF_DOWNLOAD_NO);
          }
        } else {
          this.increaseLink(links, ScreeningStatus.ANALYZED_SCREENING1YES, ScreeningStatus.SCREENING1NO);
        }
      } else {
        links.push({
          source: title,
          target: ScreeningStatus.ANALYZED_SCREENING1NO,
          value: 1
        });
      }
    });

    return {data, links, paperTitles};
  }

  setDivHeight(): void {
    /* if(this.echartsDiv) {
       const height = this.paperTitles.length * 20;
       const heightString = height + 'px';
       //TODO: Fix this
       //this.renderer.setStyle(this.echartsDiv.nativeElement, 'height', heightString);
     }*/
  }

  generateChart(flowData: {data: { name: string; }[]; links: Links[]; paperTitles: string[]}): any {
    return {
      height: flowData.paperTitles.length * 20,
      series: [{
        type: 'sankey',
        layout: 'none',
        emphasis: {
          focus: 'adjacency'
        },
        nodeAlign: 'left',
        data: flowData.data,
        links: flowData.links, lineStyle: {
          color: 'source',
          curveness: 0.5
        }
      }],
      tooltip: {
        trigger: 'item',
        triggerOn: 'mousemove'
      },
      dataZoom: [
        {
          type: 'slider'
        },
      ],
    }
  }

  private increaseLink(links: Links[], source: string, target: string) {
    const index = links.findIndex(link => link.source === source && link.target === target);
    links[index].value++;
  }

}
